import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import "firebase/compat/database";
import "firebase/compat/functions";

// MFA TOTP imports (Firebase modular SDK)
import {
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
} from "firebase/auth";

import { UserDatabaseConnector } from "../dataconnector/usersDatabaseConnector";
import { STAGE } from "../stage";
import {
  wrapFirestore,
  wrapRealTimeDb,
  wrapFunctions,
  wrapStorage,
} from "../anonymization";

// --- Mode démo : interception globale des lectures Firebase -----------------
// Patch des factories compat pour couvrir aussi les fichiers qui appellent
// firebase.firestore() / firebase.database() directement sans passer par
// initDb()/initRealTimeDb(). Les statics (FieldValue, Timestamp…) sont
// recopiés sur la factory patchée. Sans effet hors mode démo.
function patchFirebaseFactory(name, wrapInstance) {
  try {
    const original = firebase[name];
    if (typeof original !== "function" || original.__demoPatched) return;
    const patched = function (...args) {
      return wrapInstance(original.apply(firebase, args));
    };
    Object.getOwnPropertyNames(original).forEach((key) => {
      if (["length", "name", "prototype", "arguments", "caller"].includes(key)) return;
      try {
        patched[key] = original[key];
      } catch (e) {
        // prop non copiable — ignorée
      }
    });
    patched.__demoPatched = true;
    firebase[name] = patched;
  } catch (e) {
    // Patch impossible (prop non réassignable) : les init*() ci-dessous
    // restent le filet de sécurité principal.
    console.warn(`Demo mode: unable to patch firebase.${name}`, e);
  }
}
patchFirebaseFactory("firestore", wrapFirestore);
patchFirebaseFactory("database", wrapRealTimeDb);
patchFirebaseFactory("storage", wrapStorage);
patchFirebaseFactory("functions", wrapFunctions);

// firebase.app().functions('europe-west3') / firebase.app().storage(bucket) :
// on wrappe l'App retournée pour que ses accès functions/storage soient
// couverts aussi (groupPrescriptionAutomationHelpers, SpecieManagement…).
function wrapApp(app) {
  if (!app || app.__demoWrapped) return app;
  return new Proxy(app, {
    get(target, prop) {
      if (prop === "__demoWrapped") return true;
      const value = target[prop];
      if (prop === "functions" && typeof value === "function") {
        return (...args) => wrapFunctions(value.apply(target, args));
      }
      if (prop === "storage" && typeof value === "function") {
        return (...args) => wrapStorage(value.apply(target, args));
      }
      if (prop === "firestore" && typeof value === "function") {
        return (...args) => wrapFirestore(value.apply(target, args));
      }
      if (prop === "database" && typeof value === "function") {
        return (...args) => wrapRealTimeDb(value.apply(target, args));
      }
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
}
patchFirebaseFactory("app", wrapApp);

// Recette runtime (docs/qa/recette-runtime, décision de l'utilisatrice du
// 03/10) : en développement seulement, la console du navigateur atteint le SDK
// de la session connectée, dans l'ancienne syntaxe que suivent les sondes de
// cloisonnement (`firebase.firestore()…`, `firebase.app().functions(…)…`,
// `firebase.storage()…`). Même application pour la Classique et la Nouvelle.
// Jamais dans un build de production ni dans les tests.
if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
  window.firebase = firebase;
}

const firebaseConfigDev = {
  apiKey: "AIzaSyDsqPEHPLTINaJMYlcm_KCoBWdaIOdWbd4",
  authDomain: "aws-dev-606fa.firebaseapp.com",
  projectId: "aws-dev-606fa",
  storageBucket: "aws-dev-606fa.appspot.com",
  messagingSenderId: "86243752716",
  appId: "1:86243752716:web:4285b1543127e53c3b6729",
  measurementId: "G-53RFND8GT4",
};

const firebaseConfigStage = {
  apiKey: "AIzaSyCJjVnBzsxSxf2XbWsEpHkTQBnK9WAp3gM",
  authDomain: "aws-stage.firebaseapp.com",
  projectId: "aws-stage",
  storageBucket: "aws-stage.appspot.com",
  messagingSenderId: "857023048638",
  appId: "1:857023048638:web:b8921970b48d128c2c27ef",
  measurementId: "G-F7TP08Q8Y0",
  // Instance créée par scripts/setup-staging-project.sh (europe-west1, comme
  // la prod). Sans databaseURL le SDK ne trouve pas une instance hors us-central1.
  databaseURL:
    "https://aws-stage-default-rtdb.europe-west1.firebasedatabase.app",
};

const firebaseConfigQA = {
  apiKey: "AIzaSyDOe5syGMDpPiPXW0kZgCkjoNHU8iDhfS0",
  authDomain: "aws-qa-3fcb7.firebaseapp.com",
  projectId: "aws-qa-3fcb7",
  storageBucket: "aws-qa-3fcb7.appspot.com",
  messagingSenderId: "392313088238",
  appId: "1:392313088238:web:f035d85d9a8a4bcc4a6bff",
  measurementId: "G-2LPMSQXVB2",
  databaseURL:
    "https://aws-qa-3fcb7-default-rtdb.europe-west1.firebasedatabase.app",
};

const firebaseConfigProd = {
  apiKey: "AIzaSyA8oURerdW0TEvp_2ubjEg3TABlD2r0ZR4",
  authDomain: "aws-prod-3fac0.firebaseapp.com",
  projectId: "aws-prod-3fac0",
  storageBucket: "aws-prod-3fac0.appspot.com",
  messagingSenderId: "185601465462",
  appId: "1:185601465462:web:3a048766647f493dc3d357",
  measurementId: "G-33P7D6BQ5X",
  databaseURL:
    "https://aws-prod-3fac0-default-rtdb.europe-west1.firebasedatabase.app",
};

export function initDb(stage) {
  if (stage === undefined) {
    stage = STAGE;
  }
  if (!firebase.apps.length) {
    switch (stage) {
      case "dev":
        firebase.initializeApp(firebaseConfigDev);
        break;
      case "stage":
        firebase.initializeApp(firebaseConfigStage);
        break;
      case "QA":
        firebase.initializeApp(firebaseConfigQA);
        break;
      case "prod":
        firebase.initializeApp(firebaseConfigProd);
        break;
      default:
        firebase.initializeApp(firebaseConfigDev);
        break;
    }
  }
  var database = firebase.firestore();
  return wrapFirestore(database);
}

export function initRealTimeDb() {
  if (!firebase.apps.length) {
    switch (STAGE) {
      case "dev":
        firebase.initializeApp(firebaseConfigDev);
        break;
      case "stage":
        firebase.initializeApp(firebaseConfigStage);
        break;
      case "QA":
        firebase.initializeApp(firebaseConfigQA);
        break;
      case "prod":
        firebase.initializeApp(firebaseConfigProd);
        break;
      default:
        firebase.initializeApp(firebaseConfigDev);
        break;
    }
  }
  var database = firebase.database();

  return wrapRealTimeDb(database);
}

export function initAuth() {
  if (!firebase.apps.length) {
    switch (STAGE) {
      case "dev":
        firebase.initializeApp(firebaseConfigDev);
        break;
      case "stage":
        firebase.initializeApp(firebaseConfigStage);
        break;
      case "QA":
        firebase.initializeApp(firebaseConfigQA);
        break;
      case "prod":
        firebase.initializeApp(firebaseConfigProd);
        break;
      default:
        firebase.initializeApp(firebaseConfigDev);
        break;
    }
  }
  var auth = firebase.auth();
  return auth;
}

// Estampille "qui / quand" posée par les connecteurs sur chaque écriture des
// entités à historique de modifications (animals, enclosures, tournees,
// groups, sectors). Le trigger cloud onXxxModificationHistory ne fait
// confiance à lastModifiedBy que si lastModifiedAt a changé dans l'écriture.
export function getModificationStamp() {
  try {
    const auth = initAuth();
    return {
      lastModifiedBy: auth.currentUser?.uid || null,
      lastModifiedAt: Date.now(),
    };
  } catch (error) {
    return { lastModifiedBy: null, lastModifiedAt: Date.now() };
  }
}

// Estampille "créé par / créé le", posée UNE SEULE FOIS à la création d'une
// entité. Contrairement à getModificationStamp(), elle n'est jamais réécrite :
// un connecteur qui fait un set() complet doit la relire dans le doc existant
// et la réinjecter, sinon la première édition efface l'auteur.
// `createdAt` est redondant avec le createTime Firestore, mais celui-ci n'est
// pas lisible depuis un doc.data() côté client.
export function getCreationStamp() {
  let createdBy = null;
  try {
    createdBy = initAuth().currentUser?.uid || null;
  } catch (error) {
    createdBy = null;
  }

  let createdByName = null;
  try {
    const raw =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("userData")
        : null;
    if (raw) {
      const userData = JSON.parse(raw);
      // `userData` peut décrire quelqu'un d'AUTRE que l'utilisateur authentifié
      // (un superAdmin qui se connecte "en tant que" réécrit cette clé, cf.
      // ZooDetails). On ne prend le nom que s'il correspond bien à l'uid d'auth,
      // pour ne jamais attribuer une création à la mauvaise personne.
      if (!createdBy || userData.userId === createdBy) {
        createdByName =
          [userData.userFirstname, userData.userLastname]
            .filter(Boolean)
            .join(" ")
            .trim() || null;
        // Session restaurée depuis le cache sans uid d'auth : mieux vaut l'id
        // stocké côté userData que pas d'auteur du tout.
        if (!createdBy) createdBy = userData.userId || null;
      }
    }
  } catch (error) {
    createdByName = null;
  }

  return { createdBy, createdByName, createdAt: Date.now() };
}

export function initFunctions() {
  let fireApp;
  if (!firebase.apps.length) {
    switch (STAGE) {
      case "dev":
        fireApp = firebase.initializeApp(firebaseConfigDev);
        break;
      case "stage":
        fireApp = firebase.initializeApp(firebaseConfigStage);
        break;
      case "QA":
        fireApp = firebase.initializeApp(firebaseConfigQA);
        break;
      case "prod":
        fireApp = firebase.initializeApp(firebaseConfigProd);
        break;
      default:
        fireApp = firebase.initializeApp(firebaseConfigDev);
        break;
    }
  } else {
    fireApp = firebase.apps[0];
  }
  var functions = fireApp.functions("europe-west3");
  return wrapFunctions(functions);
}

export function initStorage(bucket) {
  if (!firebase.apps.length) {
    switch (STAGE) {
      case "dev":
        firebase.initializeApp(firebaseConfigDev);
        break;
      case "stage":
        firebase.initializeApp(firebaseConfigStage);
        break;
      case "QA":
        firebase.initializeApp(firebaseConfigQA);
        break;
      case "prod":
        firebase.initializeApp(firebaseConfigProd);
        break;
      default:
        firebase.initializeApp(firebaseConfigDev);
        break;
    }
  }

  if (bucket !== null) {
    return wrapStorage(firebase.app().storage(bucket));
  } else {
    return wrapStorage(firebase.storage());
  }
}

export const DATACAP = {
  maxVideoSize: 50000000,
  maxSoundSize: 5000000,
  maxPhotoSize: 2000000,
};

export async function getZooId() {
  const uConnector = new UserDatabaseConnector();
  let userData = await uConnector._getUserData();
  // Compte sans parc (invité recherche, `userZoo: null`) : pas de parc, pas d'erreur.
  return userData?.data?.userZoo?.value;
}

/**
 * Get the type of the current zoo (zoo, lab, or projet)
 * @returns {Promise<string>} The zoo type value (zoo, lab, projet)
 */
export async function getZooType() {
  const uConnector = new UserDatabaseConnector();
  let userData = await uConnector._getUserData();
  const zooId = userData.data.userZoo?.value || userData.data.zooId;

  if (!zooId) {
    return 'zoo'; // Default to zoo
  }

  // Get the zoo document to retrieve zooType
  const db = initDb();
  try {
    const zooDoc = await db.collection('zoo').doc(zooId).get();
    if (zooDoc.exists) {
      const zooData = zooDoc.data();
      return zooTypeValue(zooData.zooType);
    }
  } catch (error) {
    console.log('Error fetching zoo type:', error);
  }

  return 'zoo'; // Default to zoo
}

/** Catégorie du parc : `{ value }` (fiche V1) ou chaîne (fiche V2) ; « zoo » si absente. */
export const zooTypeValue = (zooType) => {
  const value = zooType && typeof zooType === 'object' ? zooType.value : zooType;
  return typeof value === 'string' && value.trim() ? value.trim() : 'zoo';
};

/**
 * Get both zooId and zooType for the current user
 * @returns {Promise<{zooId: string, zooType: string}>}
 */
export async function getZooIdAndType() {
  const uConnector = new UserDatabaseConnector();
  let userData = await uConnector._getUserData();
  const zooId = userData.data.userZoo?.value || userData.data.zooId;

  if (!zooId) {
    return { zooId: null, zooType: 'zoo' };
  }

  // Get the zoo document to retrieve zooType
  const db = initDb();
  try {
    const zooDoc = await db.collection('zoo').doc(zooId).get();
    if (zooDoc.exists) {
      const zooData = zooDoc.data();
      return {
        zooId: zooId,
        zooType: zooTypeValue(zooData.zooType)
      };
    }
  } catch (error) {
    console.log('Error fetching zoo type:', error);
  }

  return { zooId: zooId, zooType: 'zoo' };
}

export const backgroundImage =
  "https://github.com/akongo-welfare/static/raw/main/akongo_website/background2023.jpg";

export function getZooList() {
  if (STAGE === "prod") {
    return zooListProd;
  } else {
    return zooList;
  }
}

export async function getActiveZooList(userId) {
  let db = initDb();
  let snap = await db
    .collection("zoo")
    .where("staffAccess", "array-contains", userId)
    .get();
  let list = [];
  snap.forEach((el) => {
    let data = el.data();
    list.push({
      label: data.zooName,
      value: data.zooId,
    });
  });

  return list;
}

export function getBucket(bucketName) {
  if (STAGE === "prod") {
    if (bucketName === "cdn") {
      return "gs://cdn_prod_welfare_manager";
    } else {
      return null;
    }
  } else if (STAGE === "QA") {
    if (bucketName === "cdn") {
      return "gs://cdn_qa_welfare_manager";
    } else {
      return null;
    }
  } else {
    if (bucketName === "cdn") {
      return "gs://cdn_dev_welfare_manager";
    } else {
      return null;
    }
  }
}

export const eulaVersion = 4;

export const zooList = [
  { label: "TEST AMELIE", value: "testAmelie" },
  { label: "TEST GWEN", value: "testGwen" },
  { label: "TEST LEA", value: "testLea" },
  { label: "TEST JOSEPH", value: "testJoseph" },
  { label: "Demo", value: "demo" },
];

export const zooListProd = [
  { label: "Parc animalier et botanique - Mulhouse", value: "MulhouseZoo" },
  { label: "Domaine de branféré", value: "branfere" },
  { label: "Zoo de la Palmyre", value: "palmyre" },
  { label: "Zoo de besancon", value: "besancon" },
  { label: "Puy du fou", value: "PuyDuFou" },
  { label: "Parc de cleres", value: "cleres" },
  { label: "Zoo Sanary", value: "zooSanary" },
  { label: "Parc animalier d'Auvergne", value: "auvergne" },
  { label: "ICM", value: "ICM" },
  { label: "Poitiers", value: "poitiers" },
  { label: "Akongo", value: "akongo" },
  { label: "Beekse Bergen", value: "BeekseBergen", nemo: "HILVARENB" },
  { label: "Antwerp", value: "antwerp", nemo: "ANTWERP" },
  { label: "Planckendael", value: "planckendael", nemo: "PLANCKNDL" },
  { label: "Formation AFSA", value: "afsatraining", nemo: "" },
];

export const ZOO_LIST = [
  {
    label: "Parc animalier et botanique - Mulhouse",
    value: "MulhouseZoo",
    nemo: "MULHOUSE",
  },
  { label: "Zoo de la Palmyre", value: "palmyre", nemo: "LA PALMYR" },
  { label: "Parc de branfére", value: "branfere", nemo: "GUERNO" },
  { label: "Parc de cleres", value: "cleres", nemo: "CLERES" },
  { label: "Puy du fou", value: "PuyDuFou", nemo: "LES EPESS" },
  { label: "Zoo de besancon", value: "besancon", nemo: "BESANCON" },
  { label: "Beekse Bergen", value: "BeekseBergen", nemo: "HILVARENB" },
  { label: "Antwerp", value: "antwerp", nemo: "ANTWERP" },
  { label: "Planckendael", value: "planckendael", nemo: "PLANCKNDL" },
  { label: "Eindhoven Zoo", value: "eindhoven", nemo: "MIERLO" },
  { label: "Formation AFSA", value: "afsatraining", nemo: "" },
  { label: "Auvergne", value: "auvergne", nemo: "AUVERGNE" },

];

export const LANGUAGE_AVAILABLE = ["fr", "en", "nl", "de", "es", "it"];

export const ACTIVE_LANGUAGE = [
  { label: "FR", value: "fr", flagUrl: null },
  { label: "EN", value: "en", flagUrl: null },
  { label: "NL", value: "nl", flagUrl: null },
  { label: "DE", value: "de", flagUrl: null },
  { label: "ES", value: "es", flagUrl: null },
  { label: "IT", value: "it", flagUrl: null },
];

// Export Firebase pour accès aux constantes de Persistence
export { firebase };
export const AuthPersistence = firebase.auth.Auth.Persistence;

// Export MFA TOTP functions
export { TotpMultiFactorGenerator, getMultiFactorResolver };
